import React, { useState } from 'react';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Popper,
    Button,
    Card,
    Typography,
    TextField,
    CircularProgress,
    withStyles,
    InputAdornment,
    RadioGroup,
    Radio,
} from '@material-ui/core';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { Search as SearchIcon } from '@material-ui/icons';

const styles = (theme) => ({
    form: {
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
        maxWidth: '480px',
    },
    popover: {
        zIndex: 10000,
    },
    inputForm: {
        display: 'flex',
    },
    searchIcon: {
        width: theme.spacing() * 9,
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: fade(theme.palette.common.white, 0.85),
        '&:hover': {
            backgroundColor: fade(theme.palette.common.white, 1),
        },
        marginRight: theme.spacing(2),
        marginLeft: theme.spacing(20),
        width: '50%',
        [theme.breakpoints.down('lg')]: {
            marginRight: theme.spacing(),
            marginLeft: theme.spacing(20),
            width: '50%',
        },
        [theme.breakpoints.down('md')]: {
            marginRight: theme.spacing(),
            marginLeft: theme.spacing(3),
            width: 'auto',
        },
        [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(),
            marginLeft: theme.spacing(),
            width: 'auto',
        },
    },
    description: {
        marginTop: '10px',
        fontWeight: 'bold',
    },
    textFieldLabel: {
        marginBottom: '10px',
        fontWeight: 'bold',
    },
});

const SearchForm = (props) => {
    const { handleNPGlobalSearch, translations, classes } = props;

    const [anchorInput, setAnchorInput] = useState(null);
    const [enterBehavior, setEnterBehavior] = useState('search');
    const [searchInput, setSearchInput] = useState('');
    const [collectedInput, setCollectedInput] = useState('');

    let formRef = null;

    const handleClose = () => {
        setAnchorInput(null);
    };

    const formSettings = {
        Validation: {},
        ButtonSubmit: {
            Title: `${translations['search'] || 'Search'}`,
            Color: 'primary',
        },
        ButtonCancel: {
            Title: `${translations['cancel'] || 'Cancel'}`,
        },
    };

    const initialValues = {
        IMEI: false,
        IMEI2: false,
        SerialNumber: false,
        MEID: false,
    };

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleNPGlobalSearch({
                        searchInput: searchInput && !collectedInput ? searchInput : collectedInput.replace(/;+$/, ''),
                        ...values
                    });
                    setSearchInput('');
                    setCollectedInput('');
                    setSubmitting(false);
                }}
                enableReinitialize
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const { values, isValid, isSubmitting, handleChange, handleSubmit } = formikProps;

                    return (
                        <>
                            <TextField
                                value={searchInput}
                                className={classes.search}
                                variant="outlined"
                                InputProps={{
                                    style: { height: 35 },
                                    startAdornment: (
                                        <InputAdornment position="start" style={{ paddingLeft: 10, paddingRight: 20 }}>
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onFocus={(e) => setAnchorInput(e.currentTarget.parentElement.parentElement)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                        if (enterBehavior === 'search') {
                                            e.target.blur();
                                            formRef.dispatchEvent(
                                                new Event('submit', { cancelable: true, bubbles: true })
                                            );
                                        } else {
                                            setCollectedInput(`${collectedInput}${searchInput};`);
                                            setSearchInput('');
                                        }
                                    }
                                }}
                            />
                            {anchorInput
                                ? (() => {
                                      return (
                                          <Popper
                                              className={classes.popover}
                                              anchorEl={anchorInput}
                                              open={!!anchorInput}
                                              style={{ width: anchorInput.offsetWidth }}
                                          >
                                              <Card>
                                                  {
                                                      <form
                                                          className={classes.form}
                                                          ref={(ref) => (formRef = ref)}
                                                          onSubmit={(e) => {
                                                              e.preventDefault();
                                                              handleSubmit(e);
                                                              setAnchorInput(null);
                                                          }}
                                                      >
                                                          <Typography className={classes.description}>{`${
                                                              translations['select_enter_behavior'] ||
                                                              'Select ENTER behavior'
                                                          }`}</Typography>
                                                          <FormControl style={{ marginTop: '1rem' }}>
                                                              <RadioGroup
                                                                  row
                                                                  value={enterBehavior}
                                                                  onChange={(e) => setEnterBehavior(e.target.value)}
                                                              >
                                                                  <FormControlLabel
                                                                      value="search"
                                                                      control={<Radio />}
                                                                      label={`${translations['search'] || 'Search'}`}
                                                                  />
                                                                  <FormControlLabel
                                                                      value="collect"
                                                                      control={<Radio />}
                                                                      label={`${translations['collect'] || 'Collect'}`}
                                                                  />
                                                              </RadioGroup>
                                                          </FormControl>
                                                          <Typography className={classes.description}>{`${
                                                              translations['select_data_type'] ||
                                                              'Select the type of data'
                                                          }`}</Typography>
                                                          <FormGroup
                                                              row
                                                              style={{ marginTop: '15px', marginBottom: '15px' }}
                                                          >
                                                              <FormControlLabel
                                                                  control={
                                                                      <Checkbox
                                                                          color={'primary'}
                                                                          margin={'dense'}
                                                                          name={'IMEI'}
                                                                          checked={values.IMEI}
                                                                          onChange={handleChange}
                                                                      />
                                                                  }
                                                                  label={`${translations['imei'] || 'IMEI'}`}
                                                              />
                                                              <FormControlLabel
                                                                  control={
                                                                      <Checkbox
                                                                          color={'primary'}
                                                                          margin={'dense'}
                                                                          name={'IMEI2'}
                                                                          checked={values.IMEI2}
                                                                          onChange={handleChange}
                                                                      />
                                                                  }
                                                                  label={`${translations['imei_2'] || 'IMEI 2'}`}
                                                              />
                                                              <FormControlLabel
                                                                  control={
                                                                      <Checkbox
                                                                          color={'primary'}
                                                                          margin={'dense'}
                                                                          name={'SerialNumber'}
                                                                          checked={values.SerialNumber}
                                                                          onChange={handleChange}
                                                                      />
                                                                  }
                                                                  label={`${
                                                                      translations['serial_number'] || 'Serial number'
                                                                  }`}
                                                              />
                                                              <FormControlLabel
                                                                  control={
                                                                      <Checkbox
                                                                          color={'primary'}
                                                                          margin={'dense'}
                                                                          name={'MEID'}
                                                                          checked={values.MEID}
                                                                          onChange={handleChange}
                                                                      />
                                                                  }
                                                                  label={`${translations['meid'] || 'MEID'}`}
                                                              />
                                                          </FormGroup>
                                                          <FormControl className={classes.inputForm}>
                                                              <Typography className={classes.textFieldLabel}>{`${
                                                                  translations['global_search_placeholder'] ||
                                                                  'Enter data separated by comma or newline / enter'
                                                              }`}</Typography>
                                                              <TextField
                                                                  className={classes.input}
                                                                  name="collectedInput"
                                                                  value={collectedInput}
                                                                  variant="outlined"
                                                                  onChange={(e) => setCollectedInput(e.target.value)}
                                                                  autoComplete="false"
                                                                  multiline
                                                                  rowsMax="10"
                                                                  rows="8"
                                                                  onKeyDown={(e) => {
                                                                      // To prevent AllAssets table reloading when pressing Enter
                                                                      e.stopPropagation();
                                                                  }}
                                                              />
                                                          </FormControl>
                                                          <div style={{ display: 'flex', marginTop: '1rem' }}>
                                                              <Button
                                                                  type="button"
                                                                  style={{ marginLeft: 'auto' }}
                                                                  onClick={() => handleClose()}
                                                              >
                                                                  {formSettings.ButtonCancel.Title}
                                                              </Button>
                                                              <Button
                                                                  variant="contained"
                                                                  color={formSettings.ButtonSubmit.Color}
                                                                  type="submit"
                                                                  disabled={!isValid || !collectedInput || isSubmitting}
                                                              >
                                                                  {isSubmitting ? (
                                                                      <CircularProgress size={24} />
                                                                  ) : (
                                                                      formSettings.ButtonSubmit.Title
                                                                  )}
                                                              </Button>
                                                          </div>
                                                      </form>
                                                  }
                                              </Card>
                                          </Popper>
                                      );
                                  })()
                                : null}
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default withStyles(styles, { withTheme: true })(SearchForm);
